import { useState } from "react";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Pulse, GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError, storeToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/health";
import { startNativeGoogleLogin } from "@/lib/nativeAuth";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const body = isLogin
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(endpoint, body);
      storeToken(data.token);
      setUser(data.user);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate(data.user.onboarded ? "/app" : "/onboarding", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNativeApp()) {
      // Google refuses OAuth in embedded WebViews — use the system browser + deep link.
      startNativeGoogleLogin().catch(() => toast.error("Could not open Google sign-in"));
      return;
    }
    const redirectUrl = window.location.origin + "/app";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    // Break out of the editor/preview iframe — Google's login page refuses to render inside an iframe.
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = authUrl;
        return;
      }
    } catch (e) {
      // top is cross-origin and top-navigation is blocked — open a full tab instead.
      toast.message("Opening Google sign-in in a new tab", { description: "Ikavio is running inside a preview frame — switch to the new tab to continue." });
      window.open(authUrl, "_blank", "noopener");
      return;
    }
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 w-fit" data-testid="auth-brand-link">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </Link>
        <ThemePicker />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="clay p-8 sm:p-10 w-full max-w-md"
        >
          <div className="label-eyebrow">{isLogin ? "Welcome back" : "Create account"}</div>
          <h1 className="font-display font-light tracking-tighter text-3xl sm:text-4xl mt-2">
            {isLogin ? "Sign in to Ikavio" : "Begin your healthspan"}
          </h1>

          <button
            data-testid="google-auth-btn"
            onClick={google}
            className="mt-7 w-full flex items-center justify-center gap-3 border border-border bg-surface rounded-full py-3 font-medium hover:bg-secondary transition-colors"
          >
            <GoogleLogo size={20} weight="bold" color="var(--brand)" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted2 uppercase tracking-wider">or</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="label-eyebrow">Name</label>
                <Input data-testid="auth-name-input" className="mt-1.5 rounded-xl bg-surface h-11" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required />
              </div>
            )}
            <div>
              <label className="label-eyebrow">Email</label>
              <Input data-testid="auth-email-input" type="email" className="mt-1.5 rounded-xl bg-surface h-11" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" required />
            </div>
            <div>
              <label className="label-eyebrow">Password</label>
              <Input data-testid="auth-password-input" type="password" className="mt-1.5 rounded-xl bg-surface h-11" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required minLength={6} />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="group w-full inline-flex items-center justify-center gap-2 bg-ink text-bg rounded-full py-3.5 font-medium hover:bg-brand transition-colors disabled:opacity-60"
            >
              {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
              {!busy && <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <p className="text-sm text-muted2 mt-6 text-center">
            {isLogin ? "New to Ikavio? " : "Already have an account? "}
            <Link data-testid="auth-toggle-link" to={isLogin ? "/register" : "/login"} className="text-brand font-medium hover:underline">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
